import type { ModifierFamily, ModifierState } from "@/domain/profile-resolver/index";

interface ModifierStripProps {
  /** The modifier families the active Profile uses; the strip is hidden when empty. */
  families: ModifierFamily[];
  mods: ModifierState;
  /** Latch/unlatch one family; latches combine into the exact-combo layer. */
  onToggle: (family: ModifierFamily) => void;
  /** Clear every latch (the Base pill). */
  onBase: () => void;
}

const LABEL: Record<ModifierFamily, string> = { ctrl: "Ctrl", alt: "Alt", shift: "Shift" };

/**
 * The Layer group: a Base pill plus one independent latch per modifier the active Profile uses.
 * Latches combine into the exact-combo modifier layer; Base clears all. Text labels only - never mac
 * glyphs. Renders embedded (just the .ctl-item label + pills, no .ctl-strip surface of its own)
 * because it shares the View card with SC2 Options rather than standing as its own config strip.
 */
export function ModifierStrip({ families, mods, onToggle, onBase }: ModifierStripProps) {
  if (!families.length) return null;
  const baseOn = !(mods.ctrl || mods.alt || mods.shift);
  return (
    <div className="ctl-item">
      <span className="ctl-lbl">Layer</span>
      <div className="ctl-pills">
        <button
          type="button"
          className={`layer-pill${baseOn ? " on" : ""}`}
          title="Clear modifiers"
          onClick={onBase}
        >
          Base
        </button>
        {families.map((f) => (
          <button
            key={f}
            type="button"
            className={`layer-pill${mods[f] ? " on" : ""}`}
            title={`Toggle ${LABEL[f]} - combine for combos`}
            onClick={() => onToggle(f)}
          >
            {LABEL[f]}
          </button>
        ))}
      </div>
    </div>
  );
}
